import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, ShoppingBag, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useWishlist } from "@/hooks/useWishlist";
import { useCart } from "@/store/cart";
import { useSettings } from "@/hooks/useSettings";
import { formatCurrency } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

interface Product {
  id: string;
  name: string;
  price: number;
  sale_price: number | null;
  on_sale: boolean | null;
  image_url: string | null;
  stock: number;
}

const Wishlist = () => {
  const { ids, toggle } = useWishlist();
  const { addItem, setOpen } = useCart();
  const settings = useSettings();
  const symbol = settings.currency_symbol || "৳";
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "My Wishlist";
    (async () => {
      if (ids.length === 0) {
        setProducts([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      const { data } = await supabase
        .from("products")
        .select("id,name,price,sale_price,on_sale,image_url,stock")
        .in("id", ids);
      setProducts((data || []) as Product[]);
      setLoading(false);
    })();
  }, [ids]);

  const handleAddToCart = (p: Product) => {
    if (p.stock <= 0) {
      toast.error("Out of stock");
      return;
    }
    const price = p.on_sale && p.sale_price ? Number(p.sale_price) : Number(p.price);
    addItem({ id: p.id, name: p.name, price, image_url: p.image_url, stock: p.stock });
    toast.success("Added to cart", { description: p.name });
    setOpen(true);
  };

  return (
    <div className="container py-10">
      <div className="mb-8 flex items-center gap-3">
        <Heart className="h-7 w-7 fill-destructive text-destructive" />
        <h1 className="font-display text-3xl font-bold">My Wishlist</h1>
        <span className="ml-2 text-muted-foreground">({products.length})</span>
      </div>

      {loading ? (
        <div className="text-center py-20 text-muted-foreground">Loading…</div>
      ) : products.length === 0 ? (
        <div className="rounded-2xl border bg-card p-12 text-center">
          <Heart className="mx-auto mb-4 h-14 w-14 text-muted-foreground/40" />
          <h2 className="text-xl font-semibold mb-2">Your wishlist is empty</h2>
          <p className="text-muted-foreground mb-6">Save products you love to find them later.</p>
          <Button asChild>
            <Link to="/">Browse products</Link>
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-6 lg:grid-cols-4">
          {products.map((p) => {
            const price = p.on_sale && p.sale_price ? Number(p.sale_price) : Number(p.price);
            return (
              <div
                key={p.id}
                className="group flex flex-col overflow-hidden rounded-2xl bg-card shadow-soft transition-all hover:shadow-hover"
              >
                <Link to={`/product/${p.id}`} className="relative aspect-square overflow-hidden bg-secondary/40 p-3">
                  {p.image_url ? (
                    <img
                      src={p.image_url}
                      alt={p.name}
                      loading="lazy"
                      className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div className="grid h-full w-full place-items-center text-muted-foreground/40">
                      <ShoppingBag className="h-12 w-12" />
                    </div>
                  )}
                </Link>
                <div className="flex flex-1 flex-col gap-2 p-4">
                  <Link to={`/product/${p.id}`} className="font-medium line-clamp-2 hover:text-primary">
                    {p.name}
                  </Link>
                  <div className="font-display text-lg font-semibold">
                    {formatCurrency(price, symbol)}
                  </div>
                  <div className="mt-auto flex gap-2 pt-2">
                    <Button
                      size="sm"
                      className="flex-1 rounded-full"
                      onClick={() => handleAddToCart(p)}
                      disabled={p.stock <= 0}
                    >
                      <ShoppingBag className="mr-1.5 h-3.5 w-3.5" />
                      Add
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full"
                      onClick={() => toggle(p.id, p.name)}
                      aria-label="Remove from wishlist"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Wishlist;
